/**
 * Firestore Sync Service
 * Synchronizes tasks and user data with Firebase Firestore
 */

import { captureError, SEVERITY } from './errorService';
import {
  db,
  auth,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  serverTimestamp,
  onSnapshot
} from './firebase';

// Collection names
const COLLECTIONS = {
  USERS: 'users',
  TASKS: 'tasks',
  COMPLETED_TASKS: 'completedTasks',
  TEMPLATES: 'templates',
  PROOFS: 'proofs',
  SETTINGS: 'settings',
  MOOD_ENTRIES: 'moodEntries',
  GAMIFICATION: 'gamification',
  AGENDA_EVENTS: 'agendaEvents',
  PROFILE: 'profile',
  GO_SPORT: 'goSport'
};

/**
 * Get current user ID
 */
const getUserId = () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.uid;
};

/**
 * Get user document reference
 */
const getUserDocRef = () => {
  return doc(db, COLLECTIONS.USERS, getUserId());
};

/**
 * Get subcollection reference for user data
 */
const getUserCollection = (collectionName) => {
  return collection(db, COLLECTIONS.USERS, getUserId(), collectionName);
};

// ============== TASKS ==============

/**
 * Save a task to Firestore
 */
export const saveTask = async (task) => {
  try {
    const userId = getUserId();
    const taskRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.TASKS, task.id);

    await setDoc(taskRef, {
      ...task,
      userId,
      updatedAt: serverTimestamp(),
      createdAt: task.createdAt || serverTimestamp()
    });

    return task;
  } catch (error) {
    captureError(error, 'Error saving task to Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Save multiple tasks in batch
 */
export const saveTasks = async (tasks) => {
  try {
    const userId = getUserId();
    const batch = writeBatch(db);

    tasks.forEach(task => {
      const taskRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.TASKS, task.id);
      batch.set(taskRef, {
        ...task,
        userId,
        updatedAt: serverTimestamp(),
        createdAt: task.createdAt || serverTimestamp()
      });
    });

    await batch.commit();
    return tasks;
  } catch (error) {
    captureError(error, 'Error saving tasks batch to Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get all tasks for current user
 */
export const getTasks = async () => {
  try {
    const tasksCollection = getUserCollection(COLLECTIONS.TASKS);
    const q = query(tasksCollection, orderBy('date', 'asc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    }));
  } catch (error) {
    captureError(error, 'Error getting tasks from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get tasks for a specific date
 */
export const getTasksByDate = async (date) => {
  try {
    const tasksCollection = getUserCollection(COLLECTIONS.TASKS);
    const q = query(tasksCollection, where('date', '==', date));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    }));
  } catch (error) {
    captureError(error, 'Error getting tasks by date from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get tasks for a date range (week view)
 */
export const getTasksByDateRange = async (startDate, endDate) => {
  try {
    const tasksCollection = getUserCollection(COLLECTIONS.TASKS);
    const q = query(
      tasksCollection,
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'asc')
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    }));
  } catch (error) {
    captureError(error, 'Error getting tasks by date range from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Update a task
 */
export const updateTask = async (taskId, updates) => {
  try {
    const userId = getUserId();
    const taskRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.TASKS, taskId);

    await updateDoc(taskRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return { id: taskId, ...updates };
  } catch (error) {
    captureError(error, 'Error updating task in Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Delete a task
 */
export const deleteTask = async (taskId) => {
  try {
    const userId = getUserId();
    const taskRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.TASKS, taskId);
    await deleteDoc(taskRef);
    return taskId;
  } catch (error) {
    captureError(error, 'Error deleting task from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Delete multiple tasks in batch
 */
export const deleteTasks = async (taskIds) => {
  try {
    const userId = getUserId();
    const batch = writeBatch(db);

    taskIds.forEach(taskId => {
      const taskRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.TASKS, taskId);
      batch.delete(taskRef);
    });

    await batch.commit();
    return taskIds;
  } catch (error) {
    captureError(error, 'Error deleting tasks batch from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Subscribe to tasks in real-time
 * @param {function} onUpdate - Callback with tasks array
 * @param {function} onError - Error callback
 * @returns {function} Unsubscribe function
 */
export const subscribeToTasks = (onUpdate, onError) => {
  try {
    const userId = getUserId();
    const tasksCollection = collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.TASKS);
    const q = query(tasksCollection, orderBy('date', 'asc'));

    return onSnapshot(q,
      (snapshot) => {
        const tasks = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        onUpdate(tasks);
      },
      (error) => {
        captureError(error, 'Error in tasks subscription', SEVERITY.BACKGROUND);
        onError?.(error);
      }
    );
  } catch (error) {
    captureError(error, 'Error subscribing to tasks', SEVERITY.BACKGROUND);
    onError?.(error);
    return () => {}; // Return empty unsubscribe function
  }
};

// ============== COMPLETED TASKS ==============

/**
 * Mark a task as completed and move to completed collection
 */
export const completeTask = async (task) => {
  try {
    const userId = getUserId();
    const batch = writeBatch(db);

    // Delete from tasks collection
    const taskRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.TASKS, task.id);
    batch.delete(taskRef);

    // Add to completed collection
    const completedRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.COMPLETED_TASKS, task.id);
    batch.set(completedRef, {
      ...task,
      userId,
      completedAt: serverTimestamp(),
      completed: true
    });

    await batch.commit();
    return task;
  } catch (error) {
    captureError(error, 'Error completing task in Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get completed tasks
 */
export const getCompletedTasks = async (limitCount = 100) => {
  try {
    const completedCollection = getUserCollection(COLLECTIONS.COMPLETED_TASKS);
    const q = query(completedCollection, orderBy('completedAt', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    }));
  } catch (error) {
    captureError(error, 'Error getting completed tasks from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Save completed state (checkbox states for tasks)
 */
export const saveCompletedState = async (completedState) => {
  try {
    const userId = getUserId();
    const stateRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.SETTINGS, 'completedState');

    await setDoc(stateRef, {
      state: completedState,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return completedState;
  } catch (error) {
    captureError(error, 'Error saving completed state to Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get completed state
 */
export const getCompletedState = async () => {
  try {
    const userId = getUserId();
    const stateRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.SETTINGS, 'completedState');
    const snapshot = await getDoc(stateRef);

    if (snapshot.exists()) {
      return snapshot.data().state || {};
    }
    return {};
  } catch (error) {
    captureError(error, 'Error getting completed state from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Subscribe to completed state in real-time
 * @param {function} onUpdate - Callback with completed state object
 * @param {function} onError - Error callback
 * @returns {function} Unsubscribe function
 */
export const subscribeToCompletedState = (onUpdate, onError) => {
  try {
    const userId = getUserId();
    const stateRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.SETTINGS, 'completedState');

    return onSnapshot(stateRef,
      (snapshot) => {
        if (snapshot.exists()) {
          onUpdate(snapshot.data().state || {});
        } else {
          onUpdate({});
        }
      },
      (error) => {
        captureError(error, 'Error in completed state subscription', SEVERITY.BACKGROUND);
        onError?.(error);
      }
    );
  } catch (error) {
    captureError(error, 'Error subscribing to completed state', SEVERITY.BACKGROUND);
    onError?.(error);
    return () => {}; // Return empty unsubscribe function
  }
};

// ============== TEMPLATES ==============

/**
 * Save a custom template
 */
export const saveTemplate = async (template) => {
  try {
    const userId = getUserId();
    const templateRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.TEMPLATES, template.id);

    await setDoc(templateRef, {
      ...template,
      userId,
      updatedAt: serverTimestamp(),
      createdAt: template.createdAt || serverTimestamp()
    });

    return template;
  } catch (error) {
    captureError(error, 'Error saving template to Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get all templates
 */
export const getTemplates = async () => {
  try {
    const templatesCollection = getUserCollection(COLLECTIONS.TEMPLATES);
    const snapshot = await getDocs(templatesCollection);

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    }));
  } catch (error) {
    captureError(error, 'Error getting templates from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Delete a template
 */
export const deleteTemplate = async (templateId) => {
  try {
    const userId = getUserId();
    const templateRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.TEMPLATES, templateId);
    await deleteDoc(templateRef);
    return templateId;
  } catch (error) {
    captureError(error, 'Error deleting template from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

// ============== PROOFS ==============

/**
 * Save a proof record (file URL stored after upload to Storage)
 */
export const saveProof = async (proof) => {
  try {
    const userId = getUserId();
    const proofRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.PROOFS, proof.id);

    await setDoc(proofRef, {
      ...proof,
      userId,
      createdAt: serverTimestamp()
    });

    return proof;
  } catch (error) {
    captureError(error, 'Error saving proof to Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get all proofs
 */
export const getProofs = async () => {
  try {
    const proofsCollection = getUserCollection(COLLECTIONS.PROOFS);
    const q = query(proofsCollection, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    }));
  } catch (error) {
    captureError(error, 'Error getting proofs from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get proofs for a specific task
 */
export const getProofsByTask = async (taskId) => {
  try {
    const proofsCollection = getUserCollection(COLLECTIONS.PROOFS);
    const q = query(proofsCollection, where('taskId', '==', taskId));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    }));
  } catch (error) {
    captureError(error, 'Error getting proofs by task from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Delete a proof
 */
export const deleteProof = async (proofId) => {
  try {
    const userId = getUserId();
    const proofRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.PROOFS, proofId);
    await deleteDoc(proofRef);
    return proofId;
  } catch (error) {
    captureError(error, 'Error deleting proof from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

// ============== USER SETTINGS ==============

/**
 * Save user settings
 */
export const saveSettings = async (settings) => {
  try {
    const userId = getUserId();
    const settingsRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.SETTINGS, 'main');

    await setDoc(settingsRef, {
      ...settings,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return settings;
  } catch (error) {
    captureError(error, 'Error saving settings to Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get user settings
 */
export const getSettings = async () => {
  try {
    const userId = getUserId();
    const settingsRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.SETTINGS, 'main');
    const snapshot = await getDoc(settingsRef);

    if (snapshot.exists()) {
      return snapshot.data();
    }
    return null;
  } catch (error) {
    captureError(error, 'Error getting settings from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

// ============== GAMIFICATION ==============

/**
 * Save gamification stats
 */
export const saveGamificationStats = async (stats) => {
  try {
    const userId = getUserId();
    const statsRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.GAMIFICATION, 'stats');

    await setDoc(statsRef, {
      ...stats,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return stats;
  } catch (error) {
    captureError(error, 'Error saving gamification stats to Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get gamification stats
 */
export const getGamificationStats = async () => {
  try {
    const userId = getUserId();
    const statsRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.GAMIFICATION, 'stats');
    const snapshot = await getDoc(statsRef);

    if (snapshot.exists()) {
      return snapshot.data();
    }
    return null;
  } catch (error) {
    captureError(error, 'Error getting gamification stats from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

// ============== MOOD TRACKING ==============

/**
 * Add a mood entry
 */
export const addMoodEntry = async (entry) => {
  try {
    const userId = getUserId();
    const entryRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.MOOD_ENTRIES, entry.id);

    await setDoc(entryRef, {
      ...entry,
      userId,
      createdAt: serverTimestamp()
    });

    return entry;
  } catch (error) {
    captureError(error, 'Error adding mood entry to Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get mood history
 */
export const getMoodHistory = async (days = 30) => {
  try {
    const moodCollection = getUserCollection(COLLECTIONS.MOOD_ENTRIES);
    const q = query(moodCollection, orderBy('createdAt', 'desc'), limit(days));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    }));
  } catch (error) {
    captureError(error, 'Error getting mood history from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

// ============== SYNC UTILITIES ==============

/**
 * Sync local data to Firestore (full sync)
 */
export const syncToCloud = async (localData) => {
  try {
    const userId = getUserId();
    const batch = writeBatch(db);

    // Sync tasks
    if (localData.tasks?.length > 0) {
      localData.tasks.forEach(task => {
        const taskRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.TASKS, task.id);
        batch.set(taskRef, { ...task, userId, syncedAt: serverTimestamp() });
      });
    }

    // Sync completed tasks
    if (localData.completed?.length > 0) {
      localData.completed.forEach(task => {
        const ref = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.COMPLETED_TASKS, task.id);
        batch.set(ref, { ...task, userId, syncedAt: serverTimestamp() });
      });
    }

    // Sync templates
    if (localData.templates?.length > 0) {
      localData.templates.forEach(template => {
        const ref = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.TEMPLATES, template.id);
        batch.set(ref, { ...template, userId, syncedAt: serverTimestamp() });
      });
    }

    await batch.commit();

    // Save settings separately (not in batch)
    if (localData.settings) {
      await saveSettings(localData.settings);
    }

    if (localData.gamification) {
      await saveGamificationStats(localData.gamification);
    }

    return true;
  } catch (error) {
    captureError(error, 'Error syncing to cloud', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Fetch all cloud data
 */
export const fetchFromCloud = async () => {
  try {
    const [tasks, completed, templates, proofs, settings, gamification, moodHistory] = await Promise.all([
      getTasks(),
      getCompletedTasks(),
      getTemplates(),
      getProofs(),
      getSettings(),
      getGamificationStats(),
      getMoodHistory()
    ]);

    return {
      tasks,
      completed,
      templates,
      proofs,
      settings,
      gamification,
      moodHistory
    };
  } catch (error) {
    captureError(error, 'Error fetching from cloud', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!auth.currentUser;
};

// ============== AGENDA EVENTS ==============

/**
 * Save an agenda event to Firestore
 */
export const saveAgendaEvent = async (event) => {
  try {
    const userId = getUserId();
    const eventRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.AGENDA_EVENTS, event.id);

    await setDoc(eventRef, {
      ...event,
      userId,
      updatedAt: serverTimestamp(),
      createdAt: event.createdAt || serverTimestamp()
    });

    return event;
  } catch (error) {
    captureError(error, 'Error saving agenda event to Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Save multiple agenda events in batch
 */
export const saveAgendaEvents = async (events) => {
  try {
    const userId = getUserId();
    const batch = writeBatch(db);

    events.forEach(event => {
      const eventRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.AGENDA_EVENTS, event.id);
      batch.set(eventRef, {
        ...event,
        userId,
        updatedAt: serverTimestamp(),
        createdAt: event.createdAt || serverTimestamp()
      });
    });

    await batch.commit();
    return events;
  } catch (error) {
    captureError(error, 'Error saving agenda events batch to Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get all agenda events for current user
 */
export const getAgendaEvents = async () => {
  try {
    const userId = getUserId();
    const eventsCollection = collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.AGENDA_EVENTS);
    const q = query(eventsCollection, orderBy('date', 'asc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    }));
  } catch (error) {
    captureError(error, 'Error getting agenda events from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Delete an agenda event
 */
export const deleteAgendaEvent = async (eventId) => {
  try {
    const userId = getUserId();
    const eventRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.AGENDA_EVENTS, eventId);
    await deleteDoc(eventRef);
    return eventId;
  } catch (error) {
    captureError(error, 'Error deleting agenda event from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Subscribe to agenda events in real-time
 * @param {function} onUpdate - Callback with events array
 * @param {function} onError - Error callback
 * @returns {function} Unsubscribe function
 */
export const subscribeToAgendaEvents = (onUpdate, onError) => {
  try {
    const userId = getUserId();
    const eventsCollection = collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.AGENDA_EVENTS);
    const q = query(eventsCollection, orderBy('date', 'asc'));

    return onSnapshot(q,
      (snapshot) => {
        const events = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        onUpdate(events);
      },
      (error) => {
        captureError(error, 'Error in agenda events subscription', SEVERITY.BACKGROUND);
        onError?.(error);
      }
    );
  } catch (error) {
    captureError(error, 'Error subscribing to agenda events', SEVERITY.BACKGROUND);
    onError?.(error);
    return () => {}; // Return empty unsubscribe function
  }
};

// ============== CONFLICT RESOLUTION HELPERS ==============

/**
 * mergeTask — Compare a local and remote version of a task or agenda event.
 *
 * Returns:
 *   { result: 'push'  }  — local is newer, caller should push to Firestore
 *   { result: 'pull'  }  — remote is newer, caller should pull from Firestore
 *   { result: 'equal' }  — identical content, no action needed
 *   { result: 'conflict', local, remote }  — same version, different content
 */
export function mergeTask(local, remote) {
  const lv = local?.version  ?? 0;
  const rv = remote?.version ?? 0;

  if (lv > rv) return { result: 'push',  winner: local  };
  if (rv > lv) return { result: 'pull',  winner: remote };

  // Same version — compare meaningful content (ignore sync metadata)
  const strip = ({ version, updatedAt, deviceId, syncedAt, userId, ...rest }) => JSON.stringify(rest);
  if (strip(local) === strip(remote)) return { result: 'equal', winner: local };

  return { result: 'conflict', local, remote };
}

/**
 * Fetch a single task document from Firestore.
 * Returns null if the document does not exist.
 */
export const getTaskDoc = async (taskId) => {
  try {
    const userId = getUserId();
    const taskRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.TASKS, taskId);
    const snap = await getDoc(taskRef);
    return snap.exists() ? { ...snap.data(), id: snap.id } : null;
  } catch {
    return null;
  }
};

/**
 * Fetch a single agenda-event document from Firestore.
 * Returns null if the document does not exist.
 */
export const getAgendaEventDoc = async (eventId) => {
  try {
    const userId = getUserId();
    const eventRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.AGENDA_EVENTS, eventId);
    const snap = await getDoc(eventRef);
    return snap.exists() ? { ...snap.data(), id: snap.id } : null;
  } catch {
    return null;
  }
};

// ============== USER PROFILE ==============

/**
 * Save user profile to Firestore
 */
export const saveUserProfile = async (profile) => {
  try {
    const userId = getUserId();
    const profileRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.PROFILE, 'main');

    await setDoc(profileRef, {
      ...profile,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return profile;
  } catch (error) {
    captureError(error, 'Error saving user profile to Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get user profile from Firestore
 */
export const getUserProfile = async () => {
  try {
    const userId = getUserId();
    const profileRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.PROFILE, 'main');
    const snapshot = await getDoc(profileRef);

    if (snapshot.exists()) {
      return snapshot.data();
    }
    return null;
  } catch (error) {
    captureError(error, 'Error getting user profile from Firestore', SEVERITY.BACKGROUND);
    return null;
  }
};

// ============== SCHEDULE SETTINGS ==============

/**
 * Save schedule settings to Firestore
 */
export const saveScheduleSettings = async (settings) => {
  try {
    const userId = getUserId();
    const settingsRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.SETTINGS, 'schedule');

    await setDoc(settingsRef, {
      ...settings,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return settings;
  } catch (error) {
    captureError(error, 'Error saving schedule settings to Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get schedule settings from Firestore
 */
export const getScheduleSettings = async () => {
  try {
    const userId = getUserId();
    const settingsRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.SETTINGS, 'schedule');
    const snapshot = await getDoc(settingsRef);

    if (snapshot.exists()) {
      return snapshot.data();
    }
    return null;
  } catch (error) {
    captureError(error, 'Error getting schedule settings from Firestore', SEVERITY.BACKGROUND);
    return null;
  }
};

// ============== GO SPORT ==============

/**
 * Save Go Sport profile + data (single document per user)
 */
export const saveGoSportData = async (data) => {
  try {
    const userId = getUserId();
    const ref = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.GO_SPORT, 'profile');

    await setDoc(ref, {
      ...data,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return data;
  } catch (error) {
    captureError(error, 'Error saving Go Sport data to Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Get Go Sport data
 */
export const getGoSportData = async () => {
  try {
    const userId = getUserId();
    const ref = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.GO_SPORT, 'profile');
    const snapshot = await getDoc(ref);

    if (snapshot.exists()) {
      return snapshot.data();
    }
    return null;
  } catch (error) {
    captureError(error, 'Error getting Go Sport data from Firestore', SEVERITY.BACKGROUND);
    throw error;
  }
};

/**
 * Subscribe to Go Sport data in real-time
 */
export const subscribeToGoSportData = (onUpdate, onError) => {
  try {
    const userId = getUserId();
    const ref = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.GO_SPORT, 'profile');

    return onSnapshot(ref,
      (snapshot) => {
        if (snapshot.exists()) {
          onUpdate(snapshot.data());
        }
      },
      (error) => {
        captureError(error, 'Error in Go Sport subscription', SEVERITY.BACKGROUND);
        onError?.(error);
      }
    );
  } catch (error) {
    captureError(error, 'Error subscribing to Go Sport data', SEVERITY.BACKGROUND);
    onError?.(error);
    return () => {};
  }
};

export default {
  // Tasks
  saveTask,
  saveTasks,
  getTasks,
  getTasksByDate,
  getTasksByDateRange,
  updateTask,
  deleteTask,
  deleteTasks,
  subscribeToTasks,
  completeTask,
  getCompletedTasks,
  saveCompletedState,
  getCompletedState,
  subscribeToCompletedState,

  // Templates
  saveTemplate,
  getTemplates,
  deleteTemplate,

  // Proofs
  saveProof,
  getProofs,
  getProofsByTask,
  deleteProof,

  // Settings
  saveSettings,
  getSettings,

  // Gamification
  saveGamificationStats,
  getGamificationStats,

  // Mood
  addMoodEntry,
  getMoodHistory,

  // Profile
  saveUserProfile,
  getUserProfile,

  // Schedule
  saveScheduleSettings,
  getScheduleSettings,

  // Go Sport
  saveGoSportData,
  getGoSportData,
  subscribeToGoSportData,

  // Sync
  syncToCloud,
  fetchFromCloud,
  isAuthenticated
};
